const ICE_SERVERS: RTCIceServer[] = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun.cloudflare.com:3478" },
  { urls: "stun:stun2.l.google.com:19302" },
];

export const MAP_CHANNEL_LABEL = "klint-map-v1";

export type PeerStatus =
  | "signaling"
  | "waiting"
  | "connecting"
  | "connected"
  | "disconnected";

interface PeerLinkOptions {
  key: Uint8Array<ArrayBuffer>;
  sender: boolean;
  onChannel: (channel: RTCDataChannel) => void;
  onStatus: (status: PeerStatus) => void;
  onError: (error: Error) => void;
}

function signalServer(): string {
  const configured = import.meta.env.VITE_SIGNAL_URL as string | undefined;
  if (configured) return configured.endsWith("/") ? configured : `${configured}/`;
  // Both development and production use Peermonger's public signaling
  // service unless a deployment explicitly supplies VITE_SIGNAL_URL.
  return "wss://powser.zpl.se:2083/";
}

async function signalId(key: Uint8Array<ArrayBuffer>): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", key));
  let binary = "";
  for (const byte of digest) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/**
 * Small, Klint-specific port of Peermonger's signaling and perfect-negotiation
 * path. The shared QR key selects a two-peer room; the signaling service never
 * receives that key or any chart bytes.
 */
export class PeerLink {
  private readonly connection = new RTCPeerConnection({
    iceServers: ICE_SERVERS,
    iceCandidatePoolSize: 4,
  });
  private socket: WebSocket | null = null;
  private peerPresent = false;
  private stopped = false;
  private channel: RTCDataChannel | null = null;
  private readonly pendingSignals: unknown[] = [];
  private readonly pendingCandidates: RTCIceCandidateInit[] = [];
  private retryDelay = 1_000;
  private makingOffer = false;

  constructor(private readonly options: PeerLinkOptions) {
    this.connection.addEventListener("icecandidate", (event) => {
      if (event.candidate) this.sendSignal({ candidate: event.candidate });
    });
    this.connection.addEventListener("negotiationneeded", () => void this.negotiate());
    this.connection.addEventListener("datachannel", (event) => {
      if (this.options.sender || event.channel.label !== MAP_CHANNEL_LABEL) {
        event.channel.close();
        return;
      }
      this.useChannel(event.channel);
    });
    this.connection.addEventListener("connectionstatechange", () => {
      const state = this.connection.connectionState;
      if (state === "connected") this.options.onStatus("connected");
      else if (state === "connecting" || state === "new") {
        this.options.onStatus("connecting");
      } else if (state === "disconnected") {
        this.options.onStatus("disconnected");
      } else if (state === "failed") {
        this.fail(new Error("The direct WebRTC connection failed."));
      }
    });
  }

  async start(): Promise<void> {
    const room = await signalId(this.options.key);
    if (this.stopped) return;
    this.connectSocket(`${signalServer()}${room}`);
  }

  close(): void {
    this.stopped = true;
    this.channel?.close();
    this.socket?.close();
    this.connection.close();
  }

  private connectSocket(url: string): void {
    if (this.stopped) return;
    this.options.onStatus("signaling");
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.addEventListener("open", () => {
      this.retryDelay = 1_000;
      this.options.onStatus("waiting");
    });
    socket.addEventListener("message", (event) => void this.handleSignal(event.data));
    socket.addEventListener("error", () => {
      // The close event provides the retry/error path without reporting twice.
    });
    socket.addEventListener("close", () => {
      if (this.stopped || this.channel?.readyState === "open") return;
      const delay = this.retryDelay;
      this.retryDelay = Math.min(30_000, delay * 2);
      window.setTimeout(() => {
        if (!this.stopped && this.socket === socket) this.connectSocket(url);
      }, delay);
    });
  }

  private async handleSignal(raw: unknown): Promise<void> {
    try {
      if (typeof raw !== "string") return;
      const envelope = JSON.parse(raw) as Record<string, unknown>;
      const peerMessage = envelope.peer_msg;
      if (peerMessage && typeof peerMessage === "object") {
        await this.handlePeerMessage(peerMessage as Record<string, unknown>);
        return;
      }
      if (envelope.msg === "peer_connected") {
        this.peerPresent = true;
        this.options.onStatus("connecting");
        this.flushSignals();
        if (this.options.sender && !this.channel) {
          this.useChannel(
            this.connection.createDataChannel(MAP_CHANNEL_LABEL, { ordered: true }),
          );
        }
      } else if (envelope.msg === "peer_disconnected") {
        this.peerPresent = false;
        this.options.onStatus("disconnected");
      } else if (envelope.msg === "third_connect") {
        this.fail(new Error("This sharing link is already in use by another receiver."));
      }
    } catch (error) {
      this.fail(error instanceof Error ? error : new Error("Invalid signaling message."));
    }
  }

  private async handlePeerMessage(message: Record<string, unknown>): Promise<void> {
    if (message.description) {
      const description = message.description as RTCSessionDescriptionInit;
      // Only the exporter makes offers, so collision handling can stay simple.
      if (description.type === "offer" && this.options.sender && this.makingOffer) return;
      await this.connection.setRemoteDescription(description);
      while (this.pendingCandidates.length) {
        await this.connection.addIceCandidate(this.pendingCandidates.shift()!);
      }
      if (description.type === "offer") {
        await this.connection.setLocalDescription(await this.connection.createAnswer());
        this.sendSignal({ description: this.connection.localDescription });
      }
    }
    if (message.candidate) {
      const candidate = message.candidate as RTCIceCandidateInit;
      if (this.connection.remoteDescription) await this.connection.addIceCandidate(candidate);
      else this.pendingCandidates.push(candidate);
    }
  }

  private async negotiate(): Promise<void> {
    try {
      this.makingOffer = true;
      await this.connection.setLocalDescription(await this.connection.createOffer());
      this.sendSignal({ description: this.connection.localDescription });
    } catch (error) {
      this.fail(error instanceof Error ? error : new Error("Could not negotiate WebRTC."));
    } finally {
      this.makingOffer = false;
    }
  }

  private useChannel(channel: RTCDataChannel): void {
    this.channel = channel;
    channel.binaryType = "arraybuffer";
    channel.addEventListener("open", () => {
      this.options.onStatus("connected");
      this.options.onChannel(channel);
    });
    channel.addEventListener("error", () => {
      this.fail(new Error("The map transfer data channel failed."));
    });
  }

  private sendSignal(message: unknown): void {
    if (
      this.peerPresent &&
      this.socket &&
      this.socket.readyState === WebSocket.OPEN
    ) {
      this.socket.send(JSON.stringify(message));
    } else {
      this.pendingSignals.push(message);
    }
  }

  private flushSignals(): void {
    while (
      this.pendingSignals.length &&
      this.peerPresent &&
      this.socket?.readyState === WebSocket.OPEN
    ) {
      this.socket.send(JSON.stringify(this.pendingSignals.shift()));
    }
  }

  private fail(error: Error): void {
    if (this.stopped) return;
    this.options.onError(error);
  }
}
